import React, { FC } from 'react';
import { CommentsList } from 'app/modules/posts/modules/post/components/postCommunication/CommentsList';
import { useApprovalCommentsThreadQuery } from '../../hooks/useApprovalCommentsThreadQuery';
import { usePostCommunicationContext } from 'app/modules/posts/modules/post/components/postCommunication/PostCommunicationProvider';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { Alert, Button, mergeTailwindClasses } from '@kontentino/ui';
import CommentSkeleton from 'app/modules/posts/modules/post/components/postCommunication/comments/commentsList/CommentSkeleton';
import { useMergeRefs } from 'app/hooks/useMergeRefs';
import GoToNewestCommentButton from 'app/modules/posts/modules/post/components/postCommunication/comments/GoToNewestCommentButton';
import { PostCommunicationContent } from 'app/modules/posts/modules/post/components/postCommunication/types/postCommunicationContent';
import CommentsClientAssignedUsers from 'app/modules/posts/modules/post/components/postCommunication/comments/CommentsClientAssignedUsers';
import clsx from 'clsx';
import { useStickyApprovalRequest } from 'app/modules/posts/modules/post/components/postCommunication/comments/hooks/useStickyApprovalRequest';
import { animated } from '@react-spring/web';
import { ApprovalThreadType } from '../../types';

type Props = {
  className?: string;
  thread: ApprovalThreadType;
};

const ApprovalPostCommentsThread: FC<Props> = ({ className, thread }) => {
  const { t } = useTranslation();
  const {
    postId,
    enableRespondToApprovalRequest,
    commentsListRef,
    user: currentUser,
    commentAction,
    scrollToActiveRequestComment,
    emptyState,
  } = usePostCommunicationContext();
  const comments = useApprovalCommentsThreadQuery({
    postId,
    thread,
  });

  const activeContent =
    thread === 'internal'
      ? PostCommunicationContent.INTERNAL
      : PostCommunicationContent.CLIENT;
  const ref = useMergeRefs(commentsListRef, comments.rootRef);
  const {
    onCommentWorkflowsIsVisibleChange,
    clientTransition,
    agencyTransition,
  } = useStickyApprovalRequest({
    commentsLength: comments.data.length,
  });

  const isUserClient = currentUser.role === 'client';
  const isPinnedContent = false;
  const isReplying = commentAction === 'reply';
  const showClientAssignedUsers =
    !isReplying &&
    PostCommunicationContent.CLIENT === activeContent &&
    currentUser.role !== 'client';

  return (
    <div
      className={mergeTailwindClasses(
        clsx(
          'tw-relative tw-flex tw-max-h-[20rem] tw-min-h-[14rem] tw-flex-col tw-gap-2 tw-overflow-hidden tw-py-2',

          {
            'tw-max-h-[calc(80vh-8rem)]': isUserClient,
          },
          className,
        ),
      )}
    >
      {clientTransition(
        (style, item) =>
          item && (
            <animated.div
              style={style}
              className="tw-absolute tw-top-2 tw-z-10 tw-self-center"
              data-name="comments_client-floating-active-approval-request"
            >
              <Button
                size="small"
                className="tw-rounded-full"
                onClick={scrollToActiveRequestComment}
              >
                {t('goToApprovalRequest')}
              </Button>
            </animated.div>
          ),
      )}
      <div
        className={clsx('tw-flex tw-flex-1 tw-gap-1 tw-overflow-y-auto', {
          'tw-flex-col-reverse': !isPinnedContent,
          'tw-flex-col': isPinnedContent,
        })}
        ref={ref}
        data-name="comments_list"
      >
        <>
          {comments.isLoading && <Skeleton className="tw-h-full" />}
          {comments.error && <Alert> {t('somethingWentWrongTryAgain')}</Alert>}
          {comments.data && (
            <CommentsList
              emptyState={emptyState}
              currentUser={currentUser}
              comments={comments.data}
              enableRespondToApprovalRequest={enableRespondToApprovalRequest}
              onCommentWorkflowsIsVisibleChange={
                onCommentWorkflowsIsVisibleChange
              }
            />
          )}
        </>
        {comments.hasNextPage && (
          <div ref={comments.ref}>
            <CommentSkeleton />
          </div>
        )}
      </div>
      {agencyTransition(
        (style, item) =>
          item && (
            <animated.div
              style={style}
              className="tw-absolute tw-top-2 tw-z-10 tw-self-center"
              data-name="comments_internal-floating-active-approval-request"
            >
              <Button
                size="small"
                className="tw-rounded-full"
                onClick={scrollToActiveRequestComment}
              >
                {t('goToApprovalRequest')}
              </Button>
            </animated.div>
          ),
      )}
      <div
        className={clsx('tw-absolute tw-bottom-4 tw-self-center', {
          'tw-bottom-8': showClientAssignedUsers,
        })}
      >
        <GoToNewestCommentButton />
      </div>

      {showClientAssignedUsers && (
        <div className="tw-flex tw-pl-10">
          <CommentsClientAssignedUsers />
        </div>
      )}
    </div>
  );
};

export default ApprovalPostCommentsThread;
